import { useState, useMemo } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts/highstock";

export default function ForecastChart({ comparedStations = [] }) {
    const [showAll, setShowAll] = useState(false);

    // Build series for all compared stations
    const allSeries = useMemo(() => {
        const series = [];
        comparedStations.forEach((entry) => {
            const { station, hydroData, geoData, color } = entry;
            const name = station.station_name || `Station ${station.id}`;

            // Hydro discharge
            const hydroSorted = (hydroData || [])
                .filter(d => d.dt && d.discharge !== null)
                .map(d => [new Date(d.dt).getTime(), Number(d.discharge)])
                .sort((a, b) => a[0] - b[0]);

            let filteredHydro = hydroSorted;
            if (!showAll && hydroSorted.length > 0) {
                const lastDate = hydroSorted[hydroSorted.length - 1][0];
                const cutoff = lastDate - 30 * 24 * 60 * 60 * 1000;
                filteredHydro = hydroSorted.filter(([ts]) => ts >= cutoff);
            }

            if (filteredHydro.length > 0) {
                series.push({
                    name: comparedStations.length > 1 ? `${name} — Discharge` : "Hydrological Discharge",
                    data: filteredHydro,
                    type: "spline",
                    color: color,
                    marker: { enabled: true, radius: 2.5, fillColor: color },
                    lineWidth: 2.5,
                    zIndex: 3,
                });
            }

            // GeoGLOWS forecast (only show for single station to keep chart readable)
            if (comparedStations.length === 1) {
                const geoSorted = (geoData || [])
                    .filter(d => d.forecast_date)
                    .map(d => ({
                        date: new Date(d.forecast_date).getTime(),
                        upper: Number(d.avg_upper),
                        lower: Number(d.avg_lower),
                        median: Number(d.avg_median),
                    }))
                    .sort((a, b) => a.date - b.date);

                let filteredGeo = geoSorted;
                if (!showAll && geoSorted.length > 0) {
                    const lastDate = geoSorted[geoSorted.length - 1].date;
                    const cutoff = lastDate - 30 * 24 * 60 * 60 * 1000;
                    filteredGeo = geoSorted.filter(d => d.date >= cutoff);
                }

                if (filteredGeo.length > 0) {
                    series.push(
                        {
                            name: "Upper Forecast",
                            data: filteredGeo.map(d => [d.date, d.upper]),
                            type: "spline",
                            color: "#ff7f0e",
                            dashStyle: "ShortDash",
                            marker: { enabled: false },
                            lineWidth: 1.8,
                            zIndex: 2,
                        },
                        {
                            name: "Lower Forecast",
                            data: filteredGeo.map(d => [d.date, d.lower]),
                            type: "spline",
                            color: "#1a912a",
                            dashStyle: "ShortDash",
                            marker: { enabled: false },
                            lineWidth: 1.8,
                            zIndex: 2,
                        },
                        {
                            name: "Median Forecast",
                            data: filteredGeo.map(d => [d.date, d.median]),
                            type: "spline",
                            color: "#811b2c",
                            marker: { enabled: true, radius: 2.5, fillColor: "#811b2c", symbol: "diamond" },
                            lineWidth: 2.2,
                            zIndex: 3,
                        }
                    );
                }
            }
        });
        return series;
    }, [comparedStations, showAll]);

    // Title text
    const titleText = useMemo(() => {
        if (comparedStations.length === 0) return "Select a Station";
        if (comparedStations.length === 1) return comparedStations[0].station.station_name;
        return `Comparing ${comparedStations.length} Stations`;
    }, [comparedStations]);

    const subtitleText = useMemo(() => {
        if (comparedStations.length === 0) return "";
        if (comparedStations.length === 1) return "Discharge Analysis & GeoGLOWS Forecast";
        return comparedStations.map(e => e.station.station_name).join("  vs  ");
    }, [comparedStations]);

    const chartOptions = {
        chart: {
            type: 'spline',
            height: null,
            style: {
                fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
            },
            backgroundColor: '#ffffff',
            spacing: [20, 20, 15, 15],
        },
        title: {
            text: titleText,
            align: 'left',
            style: {
                fontSize: "16px",
                fontWeight: "700",
                color: "#1e293b",
            },
        },
        subtitle: {
            text: subtitleText,
            align: 'left',
            style: {
                fontSize: "12px",
                color: "#64748b",
                fontWeight: "500",
            },
        },
        xAxis: {
            type: "datetime",
            title: {
                text: "Date",
                style: { fontSize: "11px", color: "#64748b", fontWeight: "600" },
            },
            labels: {
                style: { fontSize: "10px", color: "#94a3b8" },
            },
            lineColor: "#e2e8f0",
            tickColor: "#e2e8f0",
            gridLineWidth: 0,
            crosshair: {
                width: 1,
                color: "rgba(37, 99, 235, 0.3)",
                dashStyle: "Dash",
            },
        },
        yAxis: {
            title: {
                text: "Discharge (m³/s)",
                style: { fontSize: "11px", color: "#64748b", fontWeight: "600" },
            },
            labels: {
                format: '{value}',
                style: { fontSize: "10px", color: "#94a3b8" },
            },
            gridLineColor: "#f1f5f9",
            gridLineDashStyle: "Dot",
        },
        tooltip: {
            crosshairs: true,
            shared: true,
            xDateFormat: "%e %b %Y",
            backgroundColor: "rgba(15, 23, 42, 0.92)",
            borderColor: "transparent",
            borderRadius: 10,
            shadow: false,
            style: {
                color: "#f8fafc",
                fontSize: "12px",
                fontWeight: "500",
            },
            headerFormat: '<span style="font-size: 11px; color: #94a3b8; font-weight: 600;">{point.key}</span><br/>',
            pointFormat: '<span style="color:{series.color};">●</span> <span style="font-weight: 600;">{series.name}:</span> <span style="font-weight: 700;">{point.y:.2f}</span> m³/s<br/>',
        },
        legend: {
            enabled: true,
            align: 'left',
            verticalAlign: 'top',
            layout: 'horizontal',
            floating: false,
            itemStyle: {
                fontSize: "11px",
                fontWeight: "600",
                color: "#475569",
            },
            itemHoverStyle: {
                color: "#1e293b",
            },
            symbolRadius: 4,
            symbolWidth: 10,
            symbolHeight: 10,
            itemDistance: 16,
        },
        credits: { enabled: false },
        plotOptions: {
            spline: {
                lineWidth: 2.5,
                marker: {
                    radius: 3,
                    lineWidth: 0,
                    symbol: 'circle',
                    states: {
                        hover: {
                            radiusPlus: 3,
                            lineWidthPlus: 2,
                        },
                    },
                },
                states: {
                    hover: {
                        lineWidthPlus: 1,
                    },
                },
            },
        },
        rangeSelector: {
            enabled: true,
            selected: 0,
            buttonTheme: {
                fill: '#f1f5f9',
                stroke: '#e2e8f0',
                'stroke-width': 1,
                r: 6,
                style: {
                    color: '#475569',
                    fontWeight: '600',
                    fontSize: '11px',
                },
                states: {
                    hover: {
                        fill: '#e2e8f0',
                        style: { color: '#1e293b' },
                    },
                    select: {
                        fill: '#2563eb',
                        style: { color: '#ffffff', fontWeight: '700' },
                    },
                },
            },
            inputStyle: {
                color: '#1e293b',
                fontWeight: '600',
                fontSize: '11px',
            },
            labelStyle: {
                color: '#64748b',
                fontWeight: '600',
                fontSize: '11px',
            },
            buttons: [
                { type: "month", count: 1, text: "1M", events: { click: () => setShowAll(false) } },
                { type: "month", count: 3, text: "3M", events: { click: () => setShowAll(false) } },
                { type: "all", text: "All", events: { click: () => setShowAll(true) } },
            ],
        },
        navigator: {
            enabled: true,
            height: 30,
            outlineColor: '#e2e8f0',
            maskFill: 'rgba(37, 99, 235, 0.08)',
            series: { color: '#2563eb', lineWidth: 1 },
            handles: {
                backgroundColor: '#f8fafc',
                borderColor: '#94a3b8',
            },
        },
        scrollbar: {
            enabled: true,
            barBackgroundColor: '#e2e8f0',
            barBorderColor: 'transparent',
            barBorderRadius: 4,
            buttonArrowColor: '#64748b',
            buttonBackgroundColor: '#f1f5f9',
            buttonBorderColor: 'transparent',
            buttonBorderRadius: 4,
            rifleColor: '#94a3b8',
            trackBackgroundColor: '#f8fafc',
            trackBorderColor: 'transparent',
            trackBorderRadius: 4,
        },

        series: allSeries,
    };

    return (
        <div style={{ borderRadius: "12px", overflow: "hidden", boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04)", border: "1px solid #f1f5f9", height: "100%" }}>
            <HighchartsReact highcharts={Highcharts} constructorType="stockChart" options={chartOptions} containerProps={{ style: { height: "100%" } }} />
        </div>
    );
}
